import { defaultLocale, type Locale } from "@/lib/i18n";

// The five Anthropic workflow patterns (kept as English technical terms across locales).
export type AgentPattern =
  | "prompt-chaining"
  | "routing"
  | "parallelization"
  | "orchestrator-workers"
  | "evaluator-optimizer";

export type UseCaseStatus = "available" | "request";

// Industries a use case serves — language-neutral tags that drive the industry
// landing pages (/solutions/ai-agents-for-{industry}/) and the hub industry filter.
export type IndustryKey = "financial-services" | "logistics" | "retail";

// Per-locale content. Card fields are always present; the full 7-part fields
// are present only when status === "available".
export interface UseCaseContent {
  industry: string; // e.g. "Lending & finance"
  title: string; // card title + page H1
  cardSummary: string; // one-line problem for the card

  // --- full page (available only) ---
  heroSubhead?: string;
  // "At a glance" scorecard shown near the hero — lets a business gauge effort vs value
  // before reading. Scores (dots) live on the structure; these are the labels/why lines.
  snapshot?: {
    valueLabel: string; // e.g. "High"
    valueWhy: string; // one line — why the value is high
    complexityLabel: string; // e.g. "Medium"
    complexityWhy: string; // one line — what drives the build difficulty
    timeToLive: string; // e.g. "~a few weeks (one outlet)"
    bestFit: string; // e.g. "F&B & retail SMEs taking orders on WhatsApp"
  };
  // Honest "buy vs build" note — when Claude's own seat product (Claude for Work +
  // connectors) already covers this use case off-the-shelf, say so and hook the reader
  // to a Claude plan. Renders as an info callout near the top; links to plans + calculator.
  offTheShelf?: {
    title: string;
    body: string;
    plansCta: string;
    costCta: string;
  };
  outcomes?: string[]; // 2–4 scannable business outcomes shown near the hero
  // Quantified before/after time impact (business ROI).
  impact?: {
    manualLabel: string;
    manualValue: string; // e.g. "~25–35 min"
    agentLabel: string;
    agentValue: string; // e.g. "~5–8 min"
    saved: string; // headline time saved + at-volume figure
    note: string; // indicative disclaimer
  };
  businessProblem?: string;
  manualWorkflow?: { steps: string[]; breaks: string };
  agenticWorkflow?: {
    narrative: string;
    steps: string[];
    inLabel?: string; // what enters the pipeline (e.g. "An application arrives")
    outLabel?: string; // what comes out (e.g. "A decision-ready summary")
    loopAround?: { index: number; label: string }; // which step loops (evaluator-optimizer)
    humanStep?: string; // human-in-the-loop terminal node
  };
  workflowDetail?: { title: string; detail: string }[]; // deep, step-by-step flow
  // A concrete before→after example rendered in Stage 3 — a real messy input and
  // the clean, structured result the agent produces. The single most persuasive
  // thing for a business buyer: turns "trust us" into "oh, that's what it does".
  workedExample?: {
    inIcon?: "whatsapp" | "doc"; // input glyph — chat vs document bundle (default: whatsapp)
    inLabel: string; // label for the input (e.g. "Customer message")
    inMessage: string; // the real, messy input (e.g. a rojak WhatsApp order)
    outLabel: string; // label for the output (e.g. "Clean order created")
    outLines: string[]; // the clean structured result, line by line
    outResult?: string; // the headline result (e.g. a total "RM 23.50")
    note?: string; // one-line footer — ties in cost/logging (e.g. "~RM 0.20 in AI cost")
  };
  howWeBuild?: string;
  // Per-use-case instance of the three agent-loop stages (gather, act, verify).
  // Renders as the sub-label under each loop pill so Stage 4 differs per use case.
  agentLoop?: [string, string, string];
  integrations?: string[];
  architecture?: string[]; // builder-facing build notes (tools, guardrails, eval harness)
  modelChoice?: { approach: string; rationale: string; costNote: string };
  models?: { task: string; model: string; why: string }[]; // which model for which task
  cost?: {
    rows: { label: string; value: string; note?: string }[];
    disclaimer: string;
  };
  evaluation?: string[];
  evaluationDetail?: {
    framework: string; // the eval framework note (Agent GPA — Goal, Plan, Action)
    baseline: string; // how the human/manual baseline is set
    dimensions: {
      dim: string; // "Goal" | "Plan" | "Action" (kept as GPA technical terms)
      desc: string;
      metrics: { name: string; measures: string; target: string }[];
    }[];
    gate: string; // the go-live gate
  };
  delivery?: string;
  ctaTitle?: string;
  ctaDescription?: string;
  faqs?: Array<{ question: string; answer: string }>;
}

// Language-agnostic structural metadata.
export interface UseCaseStructure {
  slug: string;
  status: UseCaseStatus;
  pattern: AgentPattern;
  route?: string; // available spokes only, e.g. "solutions/agent-use-cases/credit-evaluation"
  heroImage?: string; // available spokes only
  cardImage?: string; // thumbnail shown on the hub card
  relatedBlogSlug?: string;
  relatedUseCases?: string[]; // slugs
  trackLocation: string; // GA4 data-track-location
  // At-a-glance effort/impact scores (1–5, language-neutral) rendered as dot meters.
  // valueScore = business value; complexityScore = how hard it is to build.
  valueScore?: number;
  complexityScore?: number;
}

export interface AgentUseCase {
  structure: UseCaseStructure;
  content: Record<Locale, UseCaseContent>;
}

export function getUseCaseContent(
  useCase: AgentUseCase,
  locale: Locale = defaultLocale,
): UseCaseContent {
  return useCase.content[locale] ?? useCase.content[defaultLocale];
}

// Short human labels for the pattern badge (English technical terms — shown in all locales).
export const patternLabels: Record<AgentPattern, string> = {
  "prompt-chaining": "prompt chaining",
  routing: "routing",
  parallelization: "parallelization",
  "orchestrator-workers": "orchestrator-workers",
  "evaluator-optimizer": "evaluator-optimizer",
};
